import { Copy, ExternalLink, RotateCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { DeviceBadge } from '@/components/DeviceBadge';
import { loadConfig } from '@/lib/config';
import { useTheme } from '@/components/ThemeProvider';
import { MarkerDialog } from './MarkerDialog';

interface Props {
  roomId: string;
  onCopy?: (url: string) => void;
}

/**
 * QR code + shareable URL for the mobile marker-based AR client
 * (client.html). Everything here is specific to that client — the
 * marker image and "rotated client mode" (vertical-mounted kiosk phone)
 * only mean anything for a handheld/mounted camera doing marker
 * tracking, neither applies to the passthrough headset client (see
 * HeadsetCard) — hence its own card rather than sharing one with it.
 * "Rotated client mode" appends `&rotation=vertical` to the URL,
 * matching the behaviour of the old Control UI.
 */
export function MobileArCard({ roomId, onCopy }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { resolved } = useTheme();
  const [rotated, setRotated] = useState(false);
  const [url, setUrl] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await loadConfig();
      const params = new URLSearchParams({ room: roomId });
      if (rotated) params.set('rotation', 'vertical');
      const clientURL = `${cfg.syncBaseURL}/client.html?${params.toString()}`;
      if (cancelled) return;
      setUrl(clientURL);
      if (canvasRef.current) {
        await QRCode.toCanvas(canvasRef.current, clientURL, {
          width: 220,
          margin: 1,
          color: {
            dark: resolved === 'dark' ? '#f8fafc' : '#00304d',
            light: resolved === 'dark' ? '#0b1220' : '#ffffff',
          },
          errorCorrectionLevel: 'H',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roomId, rotated, resolved]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          <DeviceBadge type="mobile" variant="label" className="text-foreground" />
        </CardTitle>
        <CardDescription>Scan to join on a phone or tablet — uses the printed AR marker.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-3">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="rounded-md bg-background p-2 ring-1 ring-border transition-shadow hover:shadow-md"
        >
          <canvas ref={canvasRef} aria-label="QR code" />
        </a>

        <div className="w-full break-all rounded-md bg-muted px-3 py-2 text-center text-xs text-muted-foreground">
          {url || '…'}
        </div>

        <div className="flex w-full gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => {
              if (!url) return;
              void navigator.clipboard.writeText(url);
              onCopy?.(url);
            }}
          >
            <Copy className="h-3.5 w-3.5" /> Copy
          </Button>
          <Button variant="outline" size="sm" className="flex-1" asChild>
            <a href={url} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5" /> Open
            </a>
          </Button>
        </div>

        <label
          className="flex w-full cursor-pointer items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm"
          title="For a phone/tablet mounted vertically as a kiosk, not a handheld device"
        >
          <span className="flex items-center gap-2">
            <RotateCw className="h-3.5 w-3.5" />
            Rotated (kiosk) mode
          </span>
          <input
            type="checkbox"
            checked={rotated}
            onChange={(e) => setRotated(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
        </label>

        <MarkerDialog />
      </CardContent>
    </Card>
  );
}
